import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { creditPriceLabel } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { apiErrorText } from "./adminUi.js";

/**
 * Owner · Credit pricing (0031): the per-currency PRICE BOOK anchors, packs, named tiers,
 * validity months, and the presence-in-scoring switch. Reads are platform-admin; every
 * mutation is Owner-only server-side and audited. Prices are anchors: a pack/tier's price
 * in any currency = credits × that currency's anchor × (1 − discount).
 *
 * The price cells call creditPriceLabel — the SAME helper the gateway charge is built from.
 * They used to do their own Math.round() over whole rupees, so a 1-credit pack at 89% off
 * previewed "₹8" here and billed ₹7.77 at checkout.
 */

interface Catalog {
  priceBook: Record<string, number>;
  packs: { id: string; credits: number; discountPct: number; isActive: boolean; sortOrder: number }[];
  tiers: { id: string; name: string; credits: number; discountPct: number; isActive: boolean; sortOrder: number }[];
  validityMonths: number;
  presenceInScore: boolean;
}

const CUR_SYMBOL: Record<string, string> = { INR: "₹", USD: "$", GBP: "£", EUR: "€" };

export function CreditPricing(): ReactNode {
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const catalog = useQuery({
    queryKey: ["admin-credit-pricing"],
    queryFn: () => api.get<Catalog>("/admin/credit-pricing"),
  });
  const c = catalog.data;
  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ["admin-credit-pricing"] });
  };
  const onErr = (e: unknown): void => setErr(apiErrorText(e, "That change didn't save — Owner-only, or invalid value."));

  // Per-row edit drafts, keyed "rowId:field". The pack/tier inputs used to be uncontrolled
  // (defaultValue), so a rejected edit left the typed number in the box forever while the ₹
  // cell rendered the untouched server value — the screen showed a number that was never
  // stored. Now: the input shows the draft while editing, the ₹ cell recomputes live from it,
  // and on commit the draft is cleared whatever happens — success re-reads the store, an error
  // reverts the box to the stored value alongside the banner.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const draftKey = (id: string, field: string): string => `${id}:${field}`;
  const setDraft = (id: string, field: string, v: string): void =>
    setDrafts((d) => ({ ...d, [draftKey(id, field)]: v }));
  const clearDraft = (id: string, field: string): void =>
    setDrafts((d) => {
      const next = { ...d };
      delete next[draftKey(id, field)];
      return next;
    });
  /** Effective numeric value for live ₹ recompute: parseable draft wins, else the stored value. */
  const effNum = (id: string, field: string, server: number): number => {
    const d = drafts[draftKey(id, field)];
    if (d === undefined || d.trim() === "") return server;
    const n = Number(d);
    return Number.isFinite(n) ? n : server;
  };
  /** The exact INR a buyer is charged for this row, live from whatever is in the boxes. Clamping
   *  mirrors commitNum's validator so an in-flight "150" can't preview a negative price. A missing
   *  anchor prints "—": inventing one is how this cell disagreed with the charge in the first
   *  place, and the purchase would 500 on `price_book missing` anyway. */
  const priceCell = (id: string, credits: number, discountPct: number): string => {
    const anchor = c?.priceBook.INR;
    if (!anchor || anchor <= 0) return "—";
    return creditPriceLabel(
      effNum(id, "credits", credits),
      anchor,
      Math.min(99, Math.max(0, effNum(id, "discountPct", discountPct))),
      "INR",
    );
  };
  const commitNum = (
    kind: "pack" | "tier",
    id: string,
    field: "credits" | "discountPct",
    server: number,
    raw: string,
  ): void => {
    setErr(null);
    const nv = Number(raw);
    const invalid =
      field === "discountPct"
        ? !(Number.isFinite(nv) && nv >= 0 && nv <= 99)
        : !(Number.isInteger(nv) && nv >= 1);
    if (invalid) {
      setErr(
        field === "discountPct"
          ? "Discount must be between 0 and 99 — 100 would price it at zero."
          : "Credits must be a whole number of at least 1.",
      );
      clearDraft(id, field); // revert the box to the stored value
      return;
    }
    if (nv === server) {
      clearDraft(id, field);
      return;
    }
    const mutation = kind === "pack" ? patchPack : patchTier;
    mutation.mutate({ id, body: { [field]: nv } }, { onSettled: () => clearDraft(id, field) });
  };

  const setBook = useMutation({
    mutationFn: (v: { currency: string; perCredit: number }) => api.put("/admin/credit-pricing/book", v),
    onSuccess: refresh, onError: onErr,
  });
  const patchPack = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      api.put(`/admin/credit-pricing/packs/${v.id}`, v.body),
    onSuccess: refresh, onError: onErr,
  });
  const addPack = useMutation({
    mutationFn: () => api.post("/admin/credit-pricing/packs", { credits: 1000, discountPct: 25, sortOrder: 9 }),
    onSuccess: refresh, onError: onErr,
  });
  const delPack = useMutation({
    mutationFn: (id: string) => api.del(`/admin/credit-pricing/packs/${id}`),
    onSuccess: refresh, onError: onErr,
  });
  const patchTier = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      api.put(`/admin/credit-pricing/tiers/${v.id}`, v.body),
    onSuccess: refresh, onError: onErr,
  });
  const addTier = useMutation({
    mutationFn: () => api.post("/admin/credit-pricing/tiers", { name: "Business", credits: 1000, discountPct: 35, sortOrder: 9 }),
    onSuccess: refresh, onError: onErr,
  });
  const delTier = useMutation({
    mutationFn: (id: string) => api.del(`/admin/credit-pricing/tiers/${id}`),
    onSuccess: refresh, onError: onErr,
  });
  const setSettings = useMutation({
    mutationFn: (v: { validityMonths?: number; presenceInScore?: boolean }) =>
      api.put("/admin/credit-pricing/settings", v),
    onSuccess: refresh, onError: onErr,
  });

  if (catalog.isLoading || !c) return <section><p className="mut sm">Loading the credits catalog…</p></section>;

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Credit pricing</h1>
          <div className="sub">
            Scan 1 cr · interview 10 cr · one anchor per currency, everything scales from it · edits are live for every org
          </div>
        </div>
      </div>

      {err && <div className="banner crit" style={{ marginBottom: 12 }}><span>{err}</span></div>}

      <div className="grid g2" style={{ marginBottom: 16 }}>
        <div className="card pad">
          <h3 style={{ marginTop: 0 }}>Price book — per credit</h3>
          {Object.entries(c.priceBook).map(([cur, v]) => (
            <div key={cur} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <span style={{ width: 44, fontWeight: 700 }}>{CUR_SYMBOL[cur]} {cur}</span>
              <input
                className="input tnum"
                style={{ width: 110 }}
                type="number"
                step="0.01"
                defaultValue={v}
                onBlur={(e) => {
                  const nv = Number(e.target.value);
                  if (Number.isFinite(nv) && nv > 0 && nv !== v) setBook.mutate({ currency: cur, perCredit: nv });
                }}
              />
              <span className="mut sm">an org registered in {cur} sees only this book</span>
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
            <span style={{ fontWeight: 700 }}>Validity</span>
            <input
              className="input tnum"
              style={{ width: 80 }}
              type="number"
              min={3}
              max={36}
              defaultValue={c.validityMonths}
              onBlur={(e) => {
                const nv = Number(e.target.value);
                if (Number.isFinite(nv) && nv !== c.validityMonths) setSettings.mutate({ validityMonths: nv });
              }}
            />
            <span className="mut sm">months · FIFO — oldest credits always spend first</span>
          </div>
        </div>

        <div className="card pad">
          <h3 style={{ marginTop: 0 }}>Presence in scoring</h3>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={c.presenceInScore}
              onChange={(e) => setSettings.mutate({ presenceInScore: e.target.checked })}
            />
            <span>{c.presenceInScore ? "On · overall = 95% answers + 5% camera presence" : "Off · presence is observational only"}</span>
          </label>
          <p className="mut sm" style={{ marginBottom: 0 }}>
            Presence measures camera attention only — never appearance, accent or background.
            Candidates are told either way; the switch is audited and applies to new reports.
          </p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-h">
          <h3>Packs</h3>
          <button className="btn sm" onClick={() => addPack.mutate()}>Add pack</button>
        </div>
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Credits</th><th>Discount %</th><th>₹ price</th><th>Active</th><th /></tr></thead>
            <tbody>
              {c.packs.map((p) => (
                <tr key={p.id}>
                  <td>
                    <input className="input tnum" style={{ width: 90 }} type="number" min={1}
                      value={drafts[draftKey(p.id, "credits")] ?? String(p.credits)}
                      onChange={(e) => setDraft(p.id, "credits", e.target.value)}
                      onBlur={(e) => commitNum("pack", p.id, "credits", p.credits, e.target.value)} />
                  </td>
                  <td>
                    <input className="input tnum" style={{ width: 80 }} type="number" min={0} max={99} step={0.5}
                      value={drafts[draftKey(p.id, "discountPct")] ?? String(p.discountPct)}
                      onChange={(e) => setDraft(p.id, "discountPct", e.target.value)}
                      onBlur={(e) => commitNum("pack", p.id, "discountPct", p.discountPct, e.target.value)} />
                  </td>
                  {/* recomputes live from the draft, so the ₹ always matches the number on screen */}
                  <td className="tnum">{priceCell(p.id, p.credits, p.discountPct)}</td>
                  <td>{p.isActive ? "yes" : "off"}</td>
                  <td><button className="btn sm ghost" onClick={() => delPack.mutate(p.id)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-h">
          <h3>Subscription tiers</h3>
          <button className="btn sm" onClick={() => addTier.mutate()}>Add tier</button>
        </div>
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Name</th><th>Credits / mo</th><th>Discount %</th><th>₹ / mo</th><th /></tr></thead>
            <tbody>
              {c.tiers.map((t) => (
                <tr key={t.id}>
                  <td>
                    <input className="input" style={{ width: 140, fontWeight: 700 }}
                      value={drafts[draftKey(t.id, "name")] ?? t.name}
                      onChange={(e) => setDraft(t.id, "name", e.target.value)}
                      onBlur={(e) => {
                        const nv = e.target.value.trim();
                        if (!nv || nv === t.name) { clearDraft(t.id, "name"); return; }
                        patchTier.mutate({ id: t.id, body: { name: nv } }, { onSettled: () => clearDraft(t.id, "name") });
                      }} />
                  </td>
                  <td>
                    <input className="input tnum" style={{ width: 90 }} type="number" min={1}
                      value={drafts[draftKey(t.id, "credits")] ?? String(t.credits)}
                      onChange={(e) => setDraft(t.id, "credits", e.target.value)}
                      onBlur={(e) => commitNum("tier", t.id, "credits", t.credits, e.target.value)} />
                  </td>
                  <td>
                    <input className="input tnum" style={{ width: 80 }} type="number" min={0} max={99} step={0.5}
                      value={drafts[draftKey(t.id, "discountPct")] ?? String(t.discountPct)}
                      onChange={(e) => setDraft(t.id, "discountPct", e.target.value)}
                      onBlur={(e) => commitNum("tier", t.id, "discountPct", t.discountPct, e.target.value)} />
                  </td>
                  <td className="tnum">{priceCell(t.id, t.credits, t.discountPct)}</td>
                  <td><button className="btn sm ghost" onClick={() => delTier.mutate(t.id)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="pad mut sm" style={{ paddingTop: 8 }}>
          Tier credits roll over; cancelling keeps delivered credits. A distributor partner's share applies to every purchase here.
        </div>
      </div>
    </section>
  );
}
